/**
 * Marketplace API Service
 * Fast, lightweight API client for Strategy Marketplace
 */
import apiClient from '../api/apiClient'
import type { ApiResponse } from '../api/apiClient'

// ============================================================================
// Types
// ============================================================================

export interface MarketplaceStrategy {
  strategy_id: string
  name: string
  description: string
  creator_id: string
  creator_name: string
  category: string
  timeframe: string
  indicators: string[]
  exchanges: string[]
  instruments: string[]
  pricing: 'free' | 'paid' | 'subscription' | 'freemium'
  price: number
  subscription_fee: number
  metrics: {
    total_return: number
    sharpe_ratio: number
    max_drawdown: number
    win_rate: number
    profit_factor: number
    total_trades: number
    avg_trade: number
    backtest_period: string
  }
  avg_rating: number
  total_ratings: number
  rating_distribution: Record<number, number>
  view_count: number
  clone_count: number
  download_count: number
  status: string
  is_featured: boolean
  is_premium: boolean
  thumbnail_url?: string
  tags: string[]
  created_at: string
  updated_at: string
}

export interface CreatorProfile {
  user_id: string
  username: string
  avatar_url?: string
  bio: string
  total_strategies: number
  total_clones: number
  avg_rating: number
  total_ratings: number
  joined_date: string
  is_verified: boolean
  social_links: Record<string, string>
  strategies?: MarketplaceStrategy[]
  recent_ratings?: CreatorReview[]
}

export interface CreatorReview {
  rating_id: string
  strategy_id: string
  strategy_name: string
  user_id: string
  username: string
  rating: number
  review: string
  pros: string[]
  cons: string[]
  is_verified_purchase: boolean
  created_at: string
}

export interface StrategyRating {
  rating_id: string
  user_id: string
  username: string
  rating: number
  review: string
  pros: string[]
  cons: string[]
  is_verified_purchase: boolean
  created_at: string
}

export interface MarketplaceStats {
  total_strategies: number
  total_creators: number
  total_clones: number
  total_views: number
  avg_rating: number
  category_breakdown: Record<string, number>
  top_creators: CreatorProfile[]
}

export interface SearchFilters {
  query?: string
  category?: string
  timeframe?: string
  indicators?: string[]
  pricing?: string
  min_rating?: number
  sort_by?: 'popular' | 'recent' | 'rating' | 'return'
}

// ============================================================================
// Marketplace API Functions
// ============================================================================

// Cached responses for speed
const cache = new Map<string, { data: unknown; timestamp: number }>()
const CACHE_TTL = 60000 // 1 minute cache

function getCached<T>(key: string): T | null {
  const cached = cache.get(key)
  if (cached && Date.now() - cached.timestamp < CACHE_TTL) {
    return cached.data as T
  }
  return null
}

function setCache(key: string, data: unknown) {
  cache.set(key, { data, timestamp: Date.now() })
}

export function clearCache() {
  cache.clear()
}

// ============================================================================
// Browse & Search
// ============================================================================

export async function getStrategies(
  filters: SearchFilters = {},
  limit = 20,
  offset = 0
): Promise<ApiResponse<MarketplaceStrategy[]>> {
  const cacheKey = `strategies_${JSON.stringify(filters)}_${limit}_${offset}`
  const cached = getCached<ApiResponse<MarketplaceStrategy[]>>(cacheKey)
  if (cached) return cached

  try {
    const params = new URLSearchParams()
    if (filters.query) params.append('query', filters.query)
    if (filters.category) params.append('category', filters.category)
    if (filters.timeframe) params.append('timeframe', filters.timeframe)
    if (filters.pricing) params.append('pricing', filters.pricing)
    if (filters.sort_by) params.append('sort_by', filters.sort_by)
    params.append('limit', String(limit))
    params.append('offset', String(offset))

    const response = await apiClient.get(`/marketplace/strategies?${params}`)
    const data = response.data as ApiResponse<MarketplaceStrategy[]>
    setCache(cacheKey, data)
    return data
  } catch (error) {
    return { success: false, error: 'Failed to fetch strategies' }
  }
}

export async function searchStrategies(
  filters: SearchFilters
): Promise<ApiResponse<MarketplaceStrategy[]>> {
  return getStrategies(filters)
}

export async function getFeaturedStrategies(
  limit = 10
): Promise<ApiResponse<MarketplaceStrategy[]>> {
  const cacheKey = `featured_${limit}`
  const cached = getCached<ApiResponse<MarketplaceStrategy[]>>(cacheKey)
  if (cached) return cached

  try {
    const response = await apiClient.get(`/marketplace/featured?limit=${limit}`)
    const data = response.data as ApiResponse<MarketplaceStrategy[]>
    setCache(cacheKey, data)
    return data
  } catch (error) {
    return { success: false, error: 'Failed to fetch featured strategies' }
  }
}

export async function getTrendingStrategies(
  limit = 10
): Promise<ApiResponse<MarketplaceStrategy[]>> {
  const cacheKey = `trending_${limit}`
  const cached = getCached<ApiResponse<MarketplaceStrategy[]>>(cacheKey)
  if (cached) return cached

  try {
    const response = await apiClient.get(`/marketplace/trending?limit=${limit}`)
    const data = response.data as ApiResponse<MarketplaceStrategy[]>
    setCache(cacheKey, data)
    return data
  } catch (error) {
    return { success: false, error: 'Failed to fetch trending strategies' }
  }
}

export async function getNewReleases(
  limit = 10
): Promise<ApiResponse<MarketplaceStrategy[]>> {
  const cacheKey = `new_${limit}`
  const cached = getCached<ApiResponse<MarketplaceStrategy[]>>(cacheKey)
  if (cached) return cached

  try {
    const response = await apiClient.get(`/marketplace/new?limit=${limit}`)
    const data = response.data as ApiResponse<MarketplaceStrategy[]>
    setCache(cacheKey, data)
    return data
  } catch (error) {
    return { success: false, error: 'Failed to fetch new releases' }
  }
}

export async function getTopRatedStrategies(
  limit = 10
): Promise<ApiResponse<MarketplaceStrategy[]>> {
  try {
    const response = await apiClient.get(`/marketplace/top?limit=${limit}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch top rated strategies' }
  }
}

// ============================================================================
// Strategy Details
// ============================================================================

export async function getStrategy(
  strategyId: string
): Promise<ApiResponse<MarketplaceStrategy>> {
  try {
    const response = await apiClient.get(`/marketplace/strategy/${strategyId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch strategy' }
  }
}

export async function getStrategyRatings(
  strategyId: string
): Promise<ApiResponse<StrategyRating[]>> {
  try {
    const response = await apiClient.get(`/marketplace/strategy/${strategyId}/ratings`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch ratings' }
  }
}

export async function getAvailableIndicators(): Promise<ApiResponse<string[]>> {
  const cacheKey = 'indicators'
  const cached = getCached<ApiResponse<string[]>>(cacheKey)
  if (cached) return cached

  try {
    const response = await apiClient.get('/marketplace/indicators')
    setCache(cacheKey, response.data)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch indicators' }
  }
}

// ============================================================================
// User Actions
// ============================================================================

export async function cloneStrategy(
  strategyId: string
): Promise<ApiResponse<{ strategy_json: string }>> {
  clearCache()
  try {
    const response = await apiClient.post(`/marketplace/clone/${strategyId}`)
    return response.data
  } catch (error: unknown) {
    const err = error as { response?: { data?: { message?: string } } }
    return { 
      success: false, 
      error: err.response?.data?.message || 'Failed to clone strategy' 
    }
  }
}

export async function rateStrategy(
  strategyId: string,
  rating: number,
  review?: string,
  pros?: string[],
  cons?: string[]
): Promise<ApiResponse<void>> {
  clearCache()
  try {
    const response = await apiClient.post(`/marketplace/rate/${strategyId}`, {
      rating,
      review,
      pros,
      cons,
    })
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to submit rating' }
  }
}

export async function addToFavorites(
  strategyId: string
): Promise<ApiResponse<void>> {
  try {
    const response = await apiClient.post(`/marketplace/favorite/${strategyId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to add to favorites' }
  }
}

export async function removeFromFavorites(
  strategyId: string
): Promise<ApiResponse<void>> {
  try {
    const response = await apiClient.delete(`/marketplace/favorite/${strategyId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to remove from favorites' }
  }
}

export async function getFavorites(): Promise<ApiResponse<MarketplaceStrategy[]>> {
  try {
    const response = await apiClient.get('/marketplace/favorites')
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch favorites' }
  }
}

export async function getPurchases(): Promise<ApiResponse<MarketplaceStrategy[]>> {
  try {
    const response = await apiClient.get('/marketplace/purchases')
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch purchases' }
  }
}

// ============================================================================
// Creator
// ============================================================================

export async function getCreator(
  creatorId: string
): Promise<ApiResponse<CreatorProfile>> {
  try {
    const response = await apiClient.get(`/marketplace/creator/${creatorId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch creator' }
  }
}

export async function getCreatorStrategies(
  creatorId: string
): Promise<ApiResponse<MarketplaceStrategy[]>> {
  try {
    const response = await apiClient.get(`/marketplace/creator/${creatorId}/strategies`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch creator strategies' }
  }
}

export async function updateCreatorProfile(
  profile: Partial<CreatorProfile>
): Promise<ApiResponse<CreatorProfile>> {
  clearCache()
  try {
    const response = await apiClient.put('/marketplace/creator/profile', profile)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to update profile' }
  }
}

// ============================================================================
// Create & Publish
// ============================================================================

export async function createStrategy(
  strategy: Partial<MarketplaceStrategy>
): Promise<ApiResponse<MarketplaceStrategy>> {
  clearCache()
  try {
    const response = await apiClient.post('/marketplace/strategy', strategy)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to create strategy' }
  }
}

export async function publishStrategy(
  strategyId: string
): Promise<ApiResponse<void>> {
  clearCache()
  try {
    const response = await apiClient.post(`/marketplace/publish/${strategyId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to publish strategy' }
  }
}

export async function archiveStrategy(
  strategyId: string
): Promise<ApiResponse<void>> {
  clearCache()
  try {
    const response = await apiClient.post(`/marketplace/archive/${strategyId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to archive strategy' }
  }
}

// ============================================================================
// Wishlist / Favorites
// ============================================================================

export async function getWishlist(userId: string): Promise<ApiResponse<MarketplaceStrategy[]>> {
  try {
    const response = await apiClient.get(`/marketplace/wishlist/${userId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch wishlist' }
  }
}

export async function addToWishlist(
  userId: string,
  strategyId: string
): Promise<ApiResponse<void>> {
  try {
    const response = await apiClient.post('/marketplace/wishlist/add', {
      user_id: userId,
      strategy_id: strategyId
    })
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to add to wishlist' }
  }
}

export async function removeFromWishlist(
  userId: string,
  strategyId: string
): Promise<ApiResponse<void>> {
  try {
    const response = await apiClient.post('/marketplace/wishlist/remove', {
      user_id: userId,
      strategy_id: strategyId
    })
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to remove from wishlist' }
  }
}

export async function checkWishlist(
  userId: string,
  strategyId: string
): Promise<ApiResponse<{ is_in_wishlist: boolean }>> {
  try {
    const response = await apiClient.get(`/marketplace/wishlist/${userId}/check/${strategyId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to check wishlist' }
  }
}

// ============================================================================
// Statistics
// ============================================================================

export async function getMarketplaceStats(): Promise<ApiResponse<MarketplaceStats>> {
  const cacheKey = 'stats'
  const cached = getCached<ApiResponse<MarketplaceStats>>(cacheKey)
  if (cached) return cached

  try {
    const response = await apiClient.get('/marketplace/stats')
    setCache(cacheKey, response.data)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch stats' }
  }
}

// ============================================================================
// Categories & Filters
// ============================================================================

export const CATEGORIES = [
  { value: 'trend_following', label: 'Trend Following', icon: '📈' },
  { value: 'mean_reversion', label: 'Mean Reversion', icon: '📊' },
  { value: 'breakout', label: 'Breakout', icon: '💥' },
  { value: 'momentum', label: 'Momentum', icon: '🚀' },
  { value: 'options', label: 'Options', icon: '🎯' },
  { value: 'scalping', label: 'Scalping', icon: '⚡' },
  { value: 'swing', label: 'Swing', icon: '🌊' },
  { value: 'positional', label: 'Positional', icon: '🎪' },
] as const

export const TIMEFRAMES = [
  { value: 'intraday', label: 'Intraday' },
  { value: 'swing', label: 'Swing' },
  { value: 'positional', label: 'Positional' },
] as const

export const PRICING_MODELS = [
  { value: 'free', label: 'Free', color: '#22c55e' },
  { value: 'paid', label: 'Paid', color: '#f59e0b' },
  { value: 'subscription', label: 'Subscription', color: '#8b5cf6' },
  { value: 'freemium', label: 'Freemium', color: '#06b6d4' },
] as const

export const SORT_OPTIONS = [
  { value: 'popular', label: 'Most Popular' },
  { value: 'recent', label: 'Recently Added' },
  { value: 'rating', label: 'Highest Rated' },
  { value: 'return', label: 'Best Returns' },
] as const

// ============================================================================
// Publishing Workflow
// ============================================================================

export interface DraftStrategy {
  strategy_id: string
  name: string
  description: string
  creator_id: string
  creator_name: string
  category: string
  timeframe: string
  indicators: string[]
  exchanges: string[]
  instruments: string[]
  pricing: string
  price: number
  strategy_json: string
  tags: string[]
  status: string
  metrics: StrategyMetrics
}

export interface CreateDraftPayload {
  name: string
  description: string
  creator_id: string
  creator_name: string
  category?: string
  strategy_json?: string
  timeframe?: string
  indicators?: string[]
  exchanges?: string[]
  instruments?: string[]
  pricing?: string
  price?: number
  tags?: string[]
}

export interface UpdateDraftPayload {
  name?: string
  description?: string
  category?: string
  strategy_json?: string
  timeframe?: string
  indicators?: string[]
  pricing?: string
  price?: number
  tags?: string[]
}

export async function createDraft(payload: CreateDraftPayload): Promise<ApiResponse<DraftStrategy>> {
  try {
    const response = await apiClient.post('/marketplace/publish/draft', payload)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to create draft' }
  }
}

export async function getDrafts(userId: string): Promise<ApiResponse<DraftStrategy[]>> {
  try {
    const response = await apiClient.get(`/marketplace/publish/drafts/${userId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to fetch drafts' }
  }
}

export async function updateDraft(
  strategyId: string,
  payload: UpdateDraftPayload
): Promise<ApiResponse<void>> {
  try {
    const response = await apiClient.put(`/marketplace/publish/draft/${strategyId}`, payload)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to update draft' }
  }
}

export async function validateDraft(strategyId: string): Promise<ApiResponse<{ is_valid: boolean; errors: string[] }>> {
  try {
    const response = await apiClient.post(`/marketplace/publish/draft/${strategyId}/validate`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to validate draft' }
  }
}

export async function submitDraft(strategyId: string): Promise<ApiResponse<void>> {
  try {
    const response = await apiClient.post(`/marketplace/publish/draft/${strategyId}/submit`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to submit draft' }
  }
}

export async function deleteDraft(strategyId: string): Promise<ApiResponse<void>> {
  try {
    const response = await apiClient.delete(`/marketplace/publish/draft/${strategyId}`)
    return response.data
  } catch (error) {
    return { success: false, error: 'Failed to delete draft' }
  }
}

// ============================================================================
// Utility Functions
// ============================================================================

export function formatPrice(price: number, pricing: string): string {
  if (pricing === 'free') return 'Free'
  if (pricing === 'subscription') return `₹${price}/mo`
  return `₹${price}`
}

export function formatMetric(value: number, type: 'percent' | 'number' | 'currency'): string {
  if (type === 'percent') return `${value > 0 ? '+' : ''}${value.toFixed(1)}%`
  if (type === 'currency') return `₹${value.toFixed(0)}`
  return value.toFixed(0)
}

export function getRatingStars(rating: number): { full: number; half: boolean; empty: number } {
  const full = Math.floor(rating)
  const half = rating % 1 >= 0.5
  const empty = 5 - full - (half ? 1 : 0)
  return { full, half, empty }
}
